import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Brand = {
  name: string;
  est: string;
  note: string;
  expertise: string[];
};
const BRANDS: Brand[] = [{
  name: "B&D Doors",
  est: "Est. 1956",
  note: "Australia's largest roller door manufacturer. We carry common B&D Controll-A-Door motor parts in the van.",
  expertise: ["Controll-A-Door Diamond / Smart", "Firmadoor & Panelift", "B&D springs & cables"]
}, {
  name: "Gliderol",
  est: "Est. 1978",
  note: "Adelaide-made roller doors common in older Sydney homes. We service Glideroll motors and curtains.",
  expertise: ["GTS / GRD motor units", "Curtain slat replacement", "Spring re-tensioning"]
}, {
  name: "Steel-Line",
  est: "Est. 1986",
  note: "Premium Australian roller doors. We're factory-trained on Steel-Line motor and balance systems.",
  expertise: ["Roll-A-Door Premier", "SDO motor diagnostics", "Custom panel matching"]
}, {
  name: "Merlin Openers",
  est: "Chamberlain Group",
  note: "Most popular roller door motor brand in NSW. Capacitor and logic board faults — our daily bread.",
  expertise: ["Tiltmaster & Powerlift", "Security+ 2.0 remotes", "Battery backup systems"]
}, {
  name: "ATA Openers",
  est: "Est. 1984",
  note: "Italian-engineered openers. We service GDO-9v series and program ATA remotes on-site.",
  expertise: ["GDO-9v / NeoStar", "PTX-5 v2 remote pairing", "Sensor calibration"]
}, {
  name: "Centurion / Other",
  est: "All brands",
  note: "Centurion, Boss, Superlift, Dominator, Eco-Plus — if it's a roller door in Sydney, we've fixed it.",
  expertise: ["Universal parts inventory", "Cross-brand diagnostics", "Legacy door support"]
}];
export default function RollerBrands() {
  return <section style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Decorative editorial watermark */}
      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      left: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(160px, 20vw, 280px)",
      fontWeight: 900,
      color: CREAM_DEEP,
      opacity: 0.55,
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 0
    }}>
        BRANDS
      </div>

      {/* Top hairline rule */}
      <div aria-hidden style={{
      position: "absolute",
      top: 80,
      right: 24,
      width: 120,
      height: 1,
      background: GOLD,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 72,
        alignItems: "end"
      }} className="roller-brands-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                03 / Brands we service
              </span>
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: INK,
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              We know{" "}
              <span style={{
              color: GOLD_DARK,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                every brand
              </span>
              <br />
              on the Sydney market.
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: INK_MUTED,
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 460,
          justifySelf: "end"
        }}>
            17 years of repairs means we've worked on every major (and minor)
            roller door brand sold in Australia. Parts in the van, no guesswork.
          </p>
        </div>

        {/* === BRAND GRID === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 0,
        border: `1px solid ${CREAM_DEEP}`,
        borderRadius: 4,
        overflow: "hidden",
        background: "#ffffff"
      }} className="roller-brands-grid">
          {BRANDS.map((b, i) => <BrandCard key={i} brand={b} index={i} />)}
        </div>

        {/* === BOTTOM STRIP === */}
        <div style={{
        marginTop: 56,
        padding: "32px 40px",
        background: "#ffffff",
        border: `1px solid ${CREAM_DEEP}`,
        borderLeft: `3px solid ${GOLD}`,
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div>
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: GOLD_DARK,
            marginBottom: 8
          }}>
              Not sure what brand?
            </div>
            <div style={{
            fontFamily: FONT_HEAD,
            fontSize: 19,
            fontWeight: 600,
            color: INK,
            lineHeight: 1.3,
            letterSpacing: "-0.01em"
          }}>
              Send a photo of your door or motor — we'll identify it instantly.
            </div>
          </div>

          <a href="https://wa.me/61485000933?text=Hi%20Gold%20Garage%20Door%2C%20can%20you%20identify%20my%20roller%20door%20brand%3F" target="_blank" rel="noopener noreferrer" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 22px",
          background: NAVY,
          color: "#ffffff",
          fontFamily: FONT_HEAD,
          fontSize: 14,
          fontWeight: 700,
          borderRadius: 8,
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "background 0.2s, transform 0.2s"
        }} onMouseEnter={e => {
          e.currentTarget.style.background = "#243752";
          e.currentTarget.style.transform = "translateY(-2px)";
        }} onMouseLeave={e => {
          e.currentTarget.style.background = NAVY;
          e.currentTarget.style.transform = "translateY(0)";
        }}>
            Send photo via WhatsApp
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 1024px) {
          .roller-brands-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 700px) {
          .roller-brands-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .roller-brands-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>;
}

// === Brand Card ===
function BrandCard({
  brand,
  index
}: {
  brand: Brand;
  index: number;
}) {
  // Column borders — every 3rd needs no right border (desktop)
  const isLastCol = (index + 1) % 3 === 0;
  // Bottom borders — last row (indexes 3,4,5 in 6-item grid)
  const isLastRow = index >= 3;
  return <div className="roller-brand-card" style={{
    position: "relative",
    padding: "36px 32px",
    background: "#ffffff",
    borderRight: isLastCol ? "none" : `1px solid ${CREAM_DEEP}`,
    borderBottom: isLastRow ? "none" : `1px solid ${CREAM_DEEP}`,
    cursor: "default",
    transition: "background 0.3s",
    minHeight: 280,
    display: "flex",
    flexDirection: "column"
  }} onMouseEnter={e => {
    e.currentTarget.style.background = CREAM;
  }} onMouseLeave={e => {
    e.currentTarget.style.background = "#ffffff";
  }}>
      {/* Number marker */}
      <div style={{
      fontFamily: FONT_HEAD,
      fontSize: 12,
      fontWeight: 800,
      color: GOLD_DARK,
      letterSpacing: "0.15em",
      marginBottom: 16
    }}>
        {String(index + 1).padStart(2, "0")} / {brand.est}
      </div>

      {/* Brand name */}
      <h3 style={{
      fontFamily: FONT_HEAD,
      fontSize: 24,
      fontWeight: 800,
      color: INK,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
      margin: "0 0 14px"
    }}>
        {brand.name}
      </h3>

      {/* Note */}
      <p style={{
      fontFamily: FONT_BODY,
      fontSize: 14,
      color: INK_MUTED,
      lineHeight: 1.55,
      margin: "0 0 24px",
      flex: 1
    }}>
        {brand.note}
      </p>

      {/* Expertise list */}
      <ul style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8
    }}>
        {brand.expertise.map((item, i) => <li key={i} style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: FONT_BODY,
        fontSize: 13,
        color: INK,
        fontWeight: 500
      }}>
            <CheckCircle2 size={14} color={GOLD_DARK} strokeWidth={2.5} style={{
          flexShrink: 0
        }} />
            {item}
          </li>)}
      </ul>
    </div>;
}